import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Plus,
  X,
  Globe,
  Server,
  Mail,
} from 'lucide-react';
import { AgentTool, MCPServer, MCPToolInfo, BuiltinCredentialStatus } from '../../types';
import apiService from '../../services/api';

const EMAIL_SERVER_ID = -2;

interface MCPToolSelectorProps {
  projectId: number;
  selectedTools: AgentTool[];
  onToolsChange: (tools: AgentTool[]) => void;
}

/**
 * MCPToolSelector provides a two-phase UX for assigning MCP server tools to agents:
 *
 * 1. **Server selection** -- user picks which MCP servers to attach. Built-in servers
 *    and user-configured servers appear as compact cards with an "Add" button.
 * 2. **Tool customization** -- attached servers show in a separate section. User can
 *    expand a server to toggle individual tools on/off.
 *
 * When a server is attached, all its tools are selected by default.
 */
const MCPToolSelector: React.FC<MCPToolSelectorProps> = ({
  projectId,
  selectedTools,
  onToolsChange,
}) => {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedServers, setExpandedServers] = useState<Set<number>>(new Set());
  const [emailCredentials, setEmailCredentials] = useState<BuiltinCredentialStatus[]>([]);
  const [showEmailCredentialPicker, setShowEmailCredentialPicker] = useState(false);
  const [isRebindMode, setIsRebindMode] = useState(false);

  const fetchServers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.listMCPServers(projectId);
      setServers(data);
    } catch (err) {
      console.error('Failed to fetch MCP servers:', err);
      setError('Failed to load MCP servers');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  const fetchEmailCredentials = useCallback(async () => {
    try {
      const creds = await apiService.listBuiltinCredentials(EMAIL_SERVER_ID);
      setEmailCredentials(creds);
    } catch {
      // Silently handle
    }
  }, []);

  useEffect(() => {
    fetchServers();
    fetchEmailCredentials();
  }, [fetchServers, fetchEmailCredentials]);

  // ── Derived state ──────────────────────────────────────────────────

  /** Set of server IDs that have at least one tool selected */
  const attachedServerIds = useMemo(() => {
    const ids = new Set<number>();
    // Also collect server names from tools that lack mcp_server_id (legacy tools)
    const attachedNames = new Set<string>();
    for (const tool of selectedTools) {
      if (tool.type === 'mcp') {
        if (tool.mcp_server_id != null) {
          ids.add(tool.mcp_server_id);
        } else if (tool.name) {
          // Parse server name from "Server Name: tool_name" format
          const serverName = tool.name.split(': ')[0];
          if (serverName) attachedNames.add(serverName);
        }
      }
    }
    // Match legacy tools by server name to server IDs
    for (const server of servers) {
      if (attachedNames.has(server.name)) {
        ids.add(server.mcp_server_id);
      }
    }
    return ids;
  }, [selectedTools, servers]);

  /** Servers the agent is using */
  const attachedServers = useMemo(
    () => servers.filter((s) => attachedServerIds.has(s.mcp_server_id)),
    [servers, attachedServerIds]
  );

  /** Servers available to add */
  const availableServers = useMemo(
    () => servers.filter((s) => !attachedServerIds.has(s.mcp_server_id)),
    [servers, attachedServerIds]
  );

  // ── Handlers ───────────────────────────────────────────────────────

  /** Attach a server: select ALL its tools at once */
  const handleAttachServer = (server: MCPServer) => {
    // For email server, check credentials first
    if (server.mcp_server_id === EMAIL_SERVER_ID) {
      if (emailCredentials.length === 0) {
        alert('No email accounts configured. Please set up an email account in Integrations first.');
        return;
      }
      if (emailCredentials.length > 1) {
        setShowEmailCredentialPicker(true);
        return;
      }
    }

    // For email server with exactly one credential, auto-attach with credential_id
    const credentialId = server.mcp_server_id === EMAIL_SERVER_ID && emailCredentials.length === 1
      ? emailCredentials[0].credential_id
      : undefined;

    attachServerWithCredential(server, credentialId ?? undefined);
  };

  /** Attach a server with optional credential binding */
  const attachServerWithCredential = (server: MCPServer, credentialId?: number, credentialLabel?: string) => {
    const displayName = credentialLabel
      ? `${server.name} (${credentialLabel})`
      : server.name;

    const newTools: AgentTool[] = (server.cached_tools || []).map((tool) => ({
      type: 'mcp' as const,
      name: `${displayName}: ${tool.name}`,
      description: tool.description || '',
      mcp_server_id: server.mcp_server_id,
      mcp_tool_name: tool.name,
      ...(credentialId ? { tool_config: { credential_id: credentialId } } : {}),
    }));
    onToolsChange([...selectedTools, ...newTools]);
    setExpandedServers((prev) => new Set(prev).add(server.mcp_server_id));
    setShowEmailCredentialPicker(false);
  };

  /** Handle selecting an email credential from the picker */
  const handleSelectEmailCredential = (cred: BuiltinCredentialStatus) => {
    if (isRebindMode) {
      handleRebindEmailCredential(cred);
      return;
    }
    const emailServer = servers.find((s) => s.mcp_server_id === EMAIL_SERVER_ID);
    if (!emailServer) return;
    attachServerWithCredential(
      emailServer,
      cred.credential_id ?? undefined,
      cred.instance_label || cred.email || 'Email'
    );
  };

  /** Rebind existing email tools to a specific credential */
  const handleRebindEmailCredential = (cred: BuiltinCredentialStatus) => {
    const label = cred.instance_label || cred.email || 'Email';
    const updatedTools = selectedTools.map((tool) => {
      if (tool.mcp_server_id !== EMAIL_SERVER_ID) return tool;
      return {
        ...tool,
        name: `Email (${label}): ${tool.mcp_tool_name || tool.name?.split(': ').pop() || tool.name}`,
        tool_config: { credential_id: cred.credential_id! },
      };
    });
    onToolsChange(updatedTools);
    setShowEmailCredentialPicker(false);
    setIsRebindMode(false);
  };

  /** Check if a selected tool belongs to a given server (by ID or name prefix) */
  const toolBelongsToServer = (t: AgentTool, server: MCPServer): boolean => {
    if (t.type !== 'mcp') return false;
    if (t.mcp_server_id != null) return t.mcp_server_id === server.mcp_server_id;
    // Legacy tools without mcp_server_id: match by name prefix
    return t.name?.startsWith(`${server.name}: `) === true;
  };

  /** Check if a selected tool matches a specific tool on a server */
  const toolMatchesServerTool = (t: AgentTool, server: MCPServer, toolName: string): boolean => {
    if (t.type !== 'mcp') return false;
    if (t.mcp_server_id != null) return t.mcp_server_id === server.mcp_server_id && t.mcp_tool_name === toolName;
    // Legacy: match by full name "Server: tool_name" or by mcp_tool_name
    return t.name === `${server.name}: ${toolName}` || t.mcp_tool_name === toolName;
  };

  /** Detach a server: remove ALL its tools */
  const handleDetachServer = (serverId: number) => {
    const server = servers.find((s) => s.mcp_server_id === serverId);
    if (!server) return;
    onToolsChange(selectedTools.filter((t) => !toolBelongsToServer(t, server)));
    setExpandedServers((prev) => {
      const next = new Set(prev);
      next.delete(serverId);
      return next;
    });
  };

  /** Toggle a single tool within an attached server */
  const handleToolToggle = (server: MCPServer, tool: MCPToolInfo) => {
    const isSelected = selectedTools.some((t) => toolMatchesServerTool(t, server, tool.name));

    if (isSelected) {
      // Removing this tool -- if it's the last one, detach the server entirely
      const remaining = selectedTools.filter((t) => !toolMatchesServerTool(t, server, tool.name));
      const serverStillHasTools = remaining.some((t) => toolBelongsToServer(t, server));
      if (!serverStillHasTools) {
        setExpandedServers((prev) => {
          const next = new Set(prev);
          next.delete(server.mcp_server_id);
          return next;
        });
      }
      onToolsChange(remaining);
    } else {
      // Inherit tool_config (e.g. credential_id) and display name from sibling tools
      const siblingTool = selectedTools.find((t) => toolBelongsToServer(t, server) && t.tool_config);
      const displayName = siblingTool?.name?.split(': ')[0] || server.name;
      const newTool: AgentTool = {
        type: 'mcp',
        name: `${displayName}: ${tool.name}`,
        description: tool.description || '',
        mcp_server_id: server.mcp_server_id,
        mcp_tool_name: tool.name,
        ...(siblingTool?.tool_config ? { tool_config: { ...siblingTool.tool_config } } : {}),
      };
      onToolsChange([...selectedTools, newTool]);
    }
  };

  const toggleServerExpansion = (serverId: number) => {
    setExpandedServers((prev) => {
      const next = new Set(prev);
      if (next.has(serverId)) {
        next.delete(serverId);
      } else {
        next.add(serverId);
      }
      return next;
    });
  };

  const getSelectedToolCount = (server: MCPServer): number => {
    return selectedTools.filter((t) => toolBelongsToServer(t, server)).length;
  };

  const getTotalToolCount = (server: MCPServer): number => {
    return server.cached_tools?.length || 0;
  };

  // ── Loading / Error / Empty states ─────────────────────────────────

  if (isLoading) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-4 h-4 animate-spin text-gray-500 dark:text-gray-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading MCP servers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <div className="flex items-start space-x-2">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Failed to load MCP servers</p>
            <button
              type="button"
              onClick={fetchServers}
              className="mt-2 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-900"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (servers.length === 0) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="flex items-start space-x-2">
          <Server className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              No MCP servers available
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Add MCP servers in Settings &gt; Integrations to enable additional tools for this agent.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-4" data-testid="mcp-tool-selector" data-tutorial="mcp-tool-selector">
      {/* ── Attached servers ─────────────────────────────────────── */}
      {attachedServers.length > 0 && (
        <div className="space-y-2">
          {attachedServers.map((server) => {
            const isExpanded = expandedServers.has(server.mcp_server_id);
            const selected = getSelectedToolCount(server);
            const total = getTotalToolCount(server);

            return (
              <div
                key={server.mcp_server_id}
                className="border border-primary-200 dark:border-primary-800 rounded-lg overflow-hidden"
              >
                {/* Server header */}
                <div className="flex items-center p-3 bg-primary-50/50 dark:bg-primary-900/20">
                  <button
                    type="button"
                    onClick={() => toggleServerExpansion(server.mcp_server_id)}
                    className="flex-shrink-0 mr-2 p-0.5 rounded hover:bg-primary-100 dark:hover:bg-primary-800/30"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-primary-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-primary-500" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      {server.scope === 'built_in' ? (
                        <Globe className="w-4 h-4 text-primary-500 mr-1.5 flex-shrink-0" />
                      ) : (
                        <Server className="w-4 h-4 text-primary-500 mr-1.5 flex-shrink-0" />
                      )}
                      <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                        {server.name}
                      </span>
                      {/* Show which email account is bound to these tools */}
                      {server.mcp_server_id === EMAIL_SERVER_ID && (() => {
                        // Try tool_config.credential_id → match against emailCredentials
                        const emailTool = selectedTools.find(
                          (t) => t.mcp_server_id === EMAIL_SERVER_ID && t.tool_config && 'credential_id' in t.tool_config
                        );
                        if (emailTool) {
                          const credId = (emailTool.tool_config as { credential_id: number })?.credential_id;
                          const cred = emailCredentials.find(
                            (c) => c.credential_id === credId
                          );
                          if (cred) {
                            const label = cred.instance_label || cred.email || cred.provider;
                            return (
                              <span className="ml-2 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">
                                {label}
                              </span>
                            );
                          }
                        }
                        // If tool had a credential_id but it wasn't found, credential was deleted
                        if (emailTool) {
                          return (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsRebindMode(true);
                                setShowEmailCredentialPicker(true);
                              }}
                              className="ml-2 text-xs text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 rounded hover:bg-red-200 dark:hover:bg-red-800/40 transition-colors cursor-pointer"
                            >
                              Account deleted - click to re-select
                            </button>
                          );
                        }
                        // Fallback: extract from tool name "Email (Yahoo Mail): tool_name"
                        const namedTool = selectedTools.find(
                          (t) => t.mcp_server_id === EMAIL_SERVER_ID && t.name?.includes('(')
                        );
                        if (namedTool) {
                          const match = namedTool.name?.match(/\((.+?)\)/);
                          if (match) {
                            return (
                              <span className="ml-2 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">
                                {match[1]}
                              </span>
                            );
                          }
                        }
                        // No credential binding — infer from available credentials
                        if (emailCredentials.length === 1) {
                          const cred = emailCredentials[0];
                          return (
                            <span className="ml-2 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">
                              {cred.instance_label || cred.email || cred.provider}
                            </span>
                          );
                        }
                        if (emailCredentials.length > 0) {
                          return (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsRebindMode(true);
                                setShowEmailCredentialPicker(true);
                              }}
                              className="ml-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded hover:bg-amber-200 dark:hover:bg-amber-800/40 transition-colors cursor-pointer"
                            >
                              Select account
                            </button>
                          );
                        }
                        return null;
                      })()}
                      {server.scope === 'built_in' && (
                        <span className="ml-2 text-xs text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/40 px-1.5 py-0.5 rounded">
                          Built-in
                        </span>
                      )}
                    </div>
                  </div>

                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {selected}/{total} tools
                  </span>

                  <button
                    type="button"
                    onClick={() => handleDetachServer(server.mcp_server_id)}
                    className="ml-2 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Remove server"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Expanded tools list */}
                {isExpanded && (
                  <div className="p-3 border-t border-primary-200 dark:border-primary-800 bg-white dark:bg-cap-dark">
                    {server.cached_tools && server.cached_tools.length > 0 ? (
                      <div className="space-y-1">
                        {server.cached_tools.map((tool) => {
                          const isSelected = selectedTools.some(
                            (t) => toolMatchesServerTool(t, server, tool.name)
                          );
                          return (
                            <label
                              key={tool.name}
                              className="flex items-start space-x-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToolToggle(server, tool)}
                                className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                              />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                                  {tool.name}
                                </span>
                                {tool.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {tool.description}
                                  </p>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No tools discovered from this server.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Available servers to add ─────────────────────────────── */}
      {availableServers.length > 0 && (
        <div className="space-y-2">
          {attachedServers.length > 0 && (
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Available Servers
            </p>
          )}
          {availableServers.map((server) => {
            const toolCount = getTotalToolCount(server);
            const unhealthy =
              server.health_status === 'error' || server.health_status === 'disconnected';

            return (
              <div
                key={server.mcp_server_id}
                className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center">
                    {server.scope === 'built_in' ? (
                      <Globe className="w-4 h-4 text-primary-500 mr-1.5 flex-shrink-0" />
                    ) : (
                      <Server className="w-4 h-4 text-gray-400 mr-1.5 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                      {server.name}
                    </span>
                    {server.scope === 'built_in' && (
                      <span className="ml-2 text-xs text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/40 px-1.5 py-0.5 rounded">
                        Built-in
                      </span>
                    )}
                    {unhealthy && server.scope !== 'built_in' && (
                      <span className="ml-2 text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30 px-1.5 py-0.5 rounded">
                        {server.health_status === 'error' ? 'Error' : 'Disconnected'}
                      </span>
                    )}
                  </div>
                  {server.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {server.description}
                    </p>
                  )}
                </div>

                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
                </span>

                <button
                  type="button"
                  onClick={() => handleAttachServer(server)}
                  className="ml-3 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-800/40 rounded-md transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Email credential picker modal */}
      {showEmailCredentialPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-6 max-w-sm w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white">
                Select Email Account
              </h3>
              <button
                type="button"
                onClick={() => { setShowEmailCredentialPicker(false); setIsRebindMode(false); }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {emailCredentials.map((cred) => (
                <button
                  key={cred.credential_id}
                  type="button"
                  onClick={() => handleSelectEmailCredential(cred)}
                  className="flex items-center gap-3 w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
                >
                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-warm-white">
                      {cred.instance_label || 'Email'}
                    </div>
                    {cred.email && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {cred.email}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCPToolSelector;
